import { Trans } from '@lingui/react/macro';
import { useAtomValue } from 'jotai';

import { PatientNamePrivacy } from '../../../../common-components/composed/PatientNamePrivacy';
import { parseAndFormatDate } from '../../../../utils/parseDate';
import { capitalize } from '../../../../utils/string';
import { selectedPlanAtom } from '../../atoms';
import { formatClaimAddress } from '../helpers/formatClaimAddress';

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return 'N/A';
  try {
    return parseAndFormatDate(dateString, 'MMM d, yyyy') || 'N/A';
  } catch {
    return 'N/A';
  }
};

export const PatientInformationSection = () => {
  const currentPlan = useAtomValue(selectedPlanAtom);

  const payerIdDisplay =
    currentPlan?.commonPayerId ??
    (currentPlan?.payerId != null ? String(currentPlan.payerId) : 'N/A');

  const patientName =
    currentPlan?.patient?.displayName ??
    (currentPlan?.patient?.firstName && currentPlan?.patient?.lastName
      ? `${capitalize(currentPlan.patient.firstName.toLowerCase())} ${capitalize(currentPlan.patient.lastName.toLowerCase())}`
      : 'N/A');

  const subscriberName =
    currentPlan?.subscriber?.firstName && currentPlan?.subscriber?.lastName
      ? `${capitalize(currentPlan.subscriber.firstName.toLowerCase())} ${capitalize(currentPlan.subscriber.lastName.toLowerCase())}`
      : 'N/A';

  return (
    <div className="vai-space-y-6">
      <div className="vai-grid vai-grid-cols-6 vai-gap-8">
        {/* Row 1 */}

        {/* Patient Name */}
        <div>
          <div className="vai-text-sm vai-font-normal vai-text-muted-foreground vai-leading-none">
            <Trans>Patient Name</Trans>
          </div>
          <div className="vai-mt-3 vai-text-sm vai-font-normal vai-text-foreground">
            <PatientNamePrivacy name={patientName} />
          </div>
        </div>

        {/* DOB */}
        <div>
          <div className="vai-text-sm vai-font-normal vai-text-muted-foreground vai-leading-none">
            <Trans>DOB</Trans>
          </div>
          <div className="vai-mt-3 vai-text-sm vai-font-normal vai-text-foreground">
            {formatDate(currentPlan?.patient?.dateOfBirth)}
          </div>
        </div>

        {/* Member ID */}
        <div>
          <div className="vai-text-sm vai-font-normal vai-text-muted-foreground vai-leading-none">
            <Trans>Member ID</Trans>
          </div>
          <div className="vai-mt-3 vai-text-sm vai-font-normal vai-text-foreground">
            {currentPlan?.patient?.memberId || 'N/A'}
          </div>
        </div>

        {/* Group ID */}
        <div>
          <div className="vai-text-sm vai-font-normal vai-text-muted-foreground vai-leading-none">
            <Trans>Group ID</Trans>
          </div>
          <div className="vai-mt-3 vai-text-sm vai-font-normal vai-text-foreground">
            {currentPlan?.plan?.groupNumber || 'N/A'}
          </div>
        </div>

        {/* Group Name */}
        <div>
          <div className="vai-text-sm vai-font-normal vai-text-muted-foreground vai-leading-none">
            <Trans>Group Name</Trans>
          </div>
          <div className="vai-mt-3 vai-text-sm vai-font-normal vai-text-foreground">
            {currentPlan?.plan?.groupName || 'N/A'}
          </div>
        </div>

        {/* Subscriber Name */}
        <div>
          <div className="vai-text-sm vai-font-normal vai-text-muted-foreground vai-leading-none">
            <Trans>Subscriber Name</Trans>
          </div>
          <div className="vai-mt-3 vai-text-sm vai-font-normal vai-text-foreground">
            <PatientNamePrivacy name={subscriberName} />
          </div>
        </div>

        {/* Subscriber DOB */}
        <div>
          <div className="vai-text-sm vai-font-normal vai-text-muted-foreground vai-leading-none">
            <Trans>Subscriber DOB</Trans>
          </div>
          <div className="vai-mt-3 vai-text-sm vai-font-normal vai-text-foreground">
            {formatDate(currentPlan?.subscriber?.dateOfBirth)}
          </div>
        </div>

        {/* Row 2 */}

        {/* Effective Date */}
        <div>
          <div className="vai-text-sm vai-font-normal vai-text-muted-foreground vai-leading-none">
            <Trans>Effective Date</Trans>
          </div>
          <div className="vai-mt-3 vai-text-sm vai-font-normal vai-text-foreground">
            {formatDate(currentPlan?.plan?.effectiveDate)}
          </div>
        </div>

        {/* Policy Period */}
        <div>
          <div className="vai-text-sm vai-font-normal vai-text-muted-foreground vai-leading-none">
            <Trans>Policy Period</Trans>
          </div>
          <div className="vai-mt-3 vai-text-sm vai-font-normal vai-text-foreground">
            {formatDate(currentPlan?.plan?.policyStartDate)} –{' '}
            {formatDate(currentPlan?.plan?.policyEndDate)}
          </div>
        </div>

        {/* Benefit Period Type */}
        <div>
          <div className="vai-text-sm vai-font-normal vai-text-muted-foreground vai-leading-none">
            <Trans>Benefit Period Type</Trans>
          </div>
          <div className="vai-mt-3 vai-text-sm vai-font-normal vai-text-foreground">
            {currentPlan?.plan?.benefitPeriodType || 'N/A'}
          </div>
        </div>

        {/* Payer ID */}
        <div>
          <div className="vai-text-sm vai-font-normal vai-text-muted-foreground vai-leading-none">
            <Trans>Payer ID</Trans>
          </div>
          <div className="vai-mt-3 vai-text-sm vai-font-normal vai-text-foreground">
            {payerIdDisplay}
          </div>
        </div>

        {/* Claims Address - spans 2 columns */}
        <div className="vai-col-span-2">
          <div className="vai-text-sm vai-font-normal vai-text-muted-foreground vai-leading-none">
            <Trans>Claims Address</Trans>
          </div>
          <div className="vai-mt-3 vai-text-sm vai-font-normal vai-text-foreground">
            {formatClaimAddress(currentPlan?.claimsAddress)}
          </div>
        </div>
      </div>
    </div>
  );
};
